/**
 * StoricoPage — palmares + resoconto stagionale per ogni stagione conclusa.
 *
 * Visibile a tutti gli utenti autenticati.
 * Tabella anno/campione/CL/supercoppa + link a classifica della stagione.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuthStore } from "../../lib/auth-store";
import { type CompetizioneItem, type TipoCompetizione } from "../../hooks/useFase5";
import { useStagioni, useStagioneAttiva } from "../../hooks/useStagione";
import { useSquadre } from "../../hooks/useFase7";
import { MobileShell } from "../shell/MobileShell";
import { tr } from "../../i18n";

interface ClassificaPosizione {
  squadra_id: string;
  posizione: number;
  punti: number;
}

const TIPO_EMOJI: Record<TipoCompetizione, string> = {
  campionato: "🏆",
  champions_league: "⭐",
  supercoppa: "👑",
};

const TIPO_LABEL: Record<TipoCompetizione, string> = {
  campionato: tr("Campionato"),
  champions_league: tr("Coppa"),
  supercoppa: tr("Supercoppa"),
};

function useCompetizioniStagione(stagioneId: string | null) {
  const isAuth = !!useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["competizioni-stagione", stagioneId],
    queryFn: async () =>
      (await api.get<CompetizioneItem[]>(`/stagioni/${stagioneId}/competizioni`)).data,
    enabled: isAuth && !!stagioneId,
  });
}

function useClassifica(competizioneId: string | null) {
  const isAuth = !!useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["classifica", competizioneId],
    queryFn: async () =>
      (await api.get<ClassificaPosizione[]>(`/competizioni/${competizioneId}/classifica`)).data,
    enabled: isAuth && !!competizioneId,
    retry: false,
  });
}

function VincitoriRow({
  comp,
  squadraNomeFn,
}: {
  comp: CompetizioneItem;
  squadraNomeFn: (id: string) => string;
}) {
  const { data: classifica } = useClassifica(comp.stato === "conclusa" ? comp.id : null);
  const winner = classifica?.find((r) => r.posizione === 1);
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="text-2xl">{TIPO_EMOJI[comp.tipo]}</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-fg-muted uppercase tracking-wider">
          {TIPO_LABEL[comp.tipo]}
        </div>
        <div className="font-bold text-fg truncate">
          {winner ? squadraNomeFn(winner.squadra_id) : "—"}
        </div>
      </div>
    </div>
  );
}

function StagionePalmaresCard({
  stagioneId,
  anno,
  squadraNomeFn,
}: {
  stagioneId: string;
  anno: string;
  squadraNomeFn: (id: string) => string;
}) {
  const { data: comps } = useCompetizioniStagione(stagioneId);
  const concluse = comps?.filter((c) => c.stato === "conclusa") ?? [];

  return (
    <div className="card space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl text-accent">{anno}</h3>
        <Link to="/lega" className="text-xs text-accent hover:underline">
          {tr("Vedi classifica →")}
        </Link>
      </div>
      {concluse.length === 0 ? (
        <div className="text-xs text-fg-muted italic">{tr("Nessuna competizione conclusa.")}</div>
      ) : (
        <div className="space-y-1">
          {concluse.map((c) => (
            <VincitoriRow key={c.id} comp={c} squadraNomeFn={squadraNomeFn} />
          ))}
        </div>
      )}
    </div>
  );
}

export function StoricoPage() {
  const { data: stagioni, isLoading } = useStagioni();
  const { data: stagioneAttiva } = useStagioneAttiva();
  const { data: squadre } = useSquadre();

  const squadraNomeFn = (id: string) => squadre?.find((s) => s.id === id)?.nome ?? id.slice(0, 8);

  const concluse =
    stagioni?.filter((s) => s.stato === "conclusa").sort((a, b) => b.anno.localeCompare(a.anno)) ??
    [];

  return (
    <MobileShell logoSuffix="STORICO">
      <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Storico")}</h2>

      {isLoading && <div className="card text-fg-muted">{tr("Caricamento…")}</div>}

      {!isLoading && stagioneAttiva && (
        <div className="card space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl text-accent">
              Stagione in corso — {stagioneAttiva.anno}
            </h3>
            <span className="pill bg-success/20 text-success">{tr("in corso")}</span>
          </div>
          <p className="text-xs text-fg-muted">
            Classifica live + partite giornata sono in{" "}
            <Link to="/lega" className="text-accent hover:underline">
              {tr("Lega")}
            </Link>
            .
          </p>
        </div>
      )}

      <h3 className="font-display text-2xl text-accent tracking-wide mt-2">{tr("Palmares")}</h3>

      {!isLoading && concluse.length === 0 && (
        <div className="card text-fg-muted text-center p-8">
          <div className="text-4xl mb-2">🏆</div>
          <div>{tr("Palmares vuoto.")}</div>
          <div className="text-xs mt-2">
            {tr("Apparirà quando si concluderà la prima stagione.")}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {concluse.map((s) => (
          <StagionePalmaresCard
            key={s.id}
            stagioneId={s.id}
            anno={s.anno}
            squadraNomeFn={squadraNomeFn}
          />
        ))}
      </div>
    </MobileShell>
  );
}
